import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormArray, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

export type InputListValue = string[] | null;

/**
 * A growing list of text inputs, e.g. links (ControlValueAccessor, value: string[] | null).
 * Always shows at least one input; blank inputs stay on screen but are left out of the value.
 */
@Component({
  selector: 'input-list',
  templateUrl: './input-list.component.html',
  styleUrls: ['./input-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputListComponent), multi: true }]
})
export class InputListComponent implements ControlValueAccessor {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);

  type = input<string>('text');
  placeholder = input<string>('');
  maxLength = input<number | null>(500);
  addLabel = input<string>('InputList.add');

  protected readonly rows = new FormArray<FormControl<string>>([new FormControl('')]);
  protected readonly disabled = signal(false);

  private onChange: (value: InputListValue) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.rows.valueChanges.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(() => {
      this.onChange(this.getValue());
      this.onTouched();
    });
  }

  writeValue(value: InputListValue): void {
    const values = (value || []).filter((item) => !!item?.trim());
    // Typing writes the same values back; keep the rows, so blank inputs and focus stay.
    if (values.join('\n') === (this.getValue() || []).join('\n')) return;

    this.rows.clear({ emitEvent: false });
    (values.length ? values : ['']).forEach((item) =>
      this.rows.push(new FormControl(item), { emitEvent: false })
    );
  }

  registerOnChange(fn: (value: InputListValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) this.rows.disable({ emitEvent: false });
    else this.rows.enable({ emitEvent: false });
  }

  protected add(): void {
    if (this.disabled()) return;
    this.rows.push(new FormControl(''), { emitEvent: false });
    setTimeout(() => {
      const inputs = this.element.nativeElement.querySelectorAll('input');
      inputs[inputs.length - 1]?.focus();
    });
  }

  protected remove(index: number): void {
    if (this.disabled()) return;
    if (this.rows.length === 1) {
      this.rows.at(0).setValue('');
      return;
    }
    this.rows.removeAt(index);
  }

  private getValue(): InputListValue {
    const values = this.rows
      .getRawValue()
      .map((item) => item.trim())
      .filter(Boolean);
    return values.length ? values : null;
  }
}
